import { Event, Rsvp, RsvpReply } from "suus-api"
import moment from "moment"
import { EVENT_STATUS } from "constants/constants"

/**
 * Functions that do some business logic on the Suus-API Event objects.
 * This is a static class, so you can use it without instantiating it.
 * The use of static class is meant to improve the discoverability of the functions.
 */

export class EventFunctions {
  static hasEventPassed(event: Event) {
    const daysSinceEnd = moment()
      .utc()
      .diff(event.end_at || event.start_at, "days")
    return daysSinceEnd > 0
  }

  static isEventAttendableOnline(event: Event) {
    const daysPassedSinceStart = moment().utc().diff(event.start_at, "days")
    // @Stephan Biastoch: I copied the logic from the store. daysPassedSinceStart >= -1 seems wrong.
    // Shouldn't it be daysPassedSinceStart < 0 or daysPassedSinceStart <= -1?
    return event.online && daysPassedSinceStart >= -1
  }

  /**
   * Determines the status of the event attendance for a given event and rsvp object.
   * If the event or rsvp object is missing, the function will return the default status REGISTER.
   *
   * @param event
   * @param rsvp
   * @returns EVENT_STATUS
   */
  static determineEventAttendanceStatus(
    event?: Event,
    rsvp?: Rsvp
  ): EVENT_STATUS {
    // DEFAULT CASE if data is incomplete
    if (!rsvp || !event) {
      return EVENT_STATUS.REGISTER
    }

    // EVENT_PASSED
    const isEventPassed = EventFunctions.hasEventPassed(event)
    if (isEventPassed) {
      return EVENT_STATUS.EVENT_PASSED
    }

    // DEREGISTERED
    const hasUserDeclined = rsvp.type === RsvpReply.Decline
    if (hasUserDeclined) {
      return EVENT_STATUS.DEREGISTERED
    }

    const hasUserAttended = rsvp.type === RsvpReply.Attend

    // ATTEND_ONLINE_NOW
    const isEventAttendableOnline =
      EventFunctions.isEventAttendableOnline(event)
    if (hasUserAttended && isEventAttendableOnline) {
      return EVENT_STATUS.ATTEND_ONLINE_NOW
    }

    // REGISTRATION_FINISHED
    const isRegistrationFinished =
      !event.allow_guests || rsvp.finished_registration
    if (hasUserAttended && isRegistrationFinished) {
      return EVENT_STATUS.FINISHED_REGISTRATION
    }

    // REGISTERED
    if (hasUserAttended) {
      return EVENT_STATUS.REGISTERED
    }

    // DEFAULT CASE
    return EVENT_STATUS.REGISTER
  }

  static getUrl(event: Event) {
    return `/events/${event.id}`
  }
}
